import { useEffect, useMemo, useState } from "react";
import {
  ADMIN_TOKEN_KEY,
  adminCreateProduct,
  adminDeleteProduct,
  adminListProducts,
  adminGetProductStats,
  adminLogin,
  adminReorderProducts,
  adminUpdateProduct,
  adminUploadFile,
  getProduct,
} from "@/lib/api";
import { OrdersDashboard } from "@/pages/AdminOrders";
import { ContentEditor } from "@/pages/AdminContent";
import AdminEmails from "@/pages/AdminEmails";
import AdminJournal from "@/pages/AdminJournal";
import AdminNewsletter from "@/pages/AdminNewsletter";
import AdminTraffic from "@/pages/AdminTraffic";
import AdminVouchers from "@/pages/AdminVouchers";
import AdminCategories from "@/pages/AdminCategories";
import AdminSale from "@/pages/AdminSale";
import { ImageFramingEditor } from "@/components/ImageFramingEditor";
import { imgPos } from "@/lib/utils";
import { useCatalog } from "@/context/CatalogContext";

const slugify = (s) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "")
    .slice(0, 60);

const nextSku = (category, existing) => {
  const prefix = "GL-" + (category || "xx").slice(0, 2).toUpperCase() + "-";
  const usedNumbers = existing
    .filter((p) => (p.sku || "").startsWith(prefix))
    .map((p) => parseInt(String(p.sku).replace(prefix, ""), 10) || 0);
  const next = (usedNumbers.length ? Math.max(...usedNumbers) : 100) + 1;
  return prefix + String(next).padStart(3, "0");
};

// ─── Login ────────────────────────────────────────────────────────
const AdminLogin = ({ onLoggedIn }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { token } = await adminLogin(pw);
      localStorage.setItem(ADMIN_TOKEN_KEY, token);
      onLoggedIn();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Wrong password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFF3E6] p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white p-8 rounded-2xl shadow-sm border border-[#F1DCC5]"
      >
        <div className="font-serif-display text-3xl text-[#241C18]">GOYA · Admin</div>
        <p className="text-sm text-[#8A7568] mt-1">Enter the shared admin password to continue.</p>
        <input
          data-testid="admin-login-password"
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Password"
          autoFocus
          className="mt-5 w-full px-4 py-3 rounded-full border border-[#F1DCC5] outline-none focus:border-[var(--gl-orange)] text-[#241C18]"
        />
        {err && (
          <div data-testid="admin-login-error" className="mt-3 text-sm text-red-600">
            {err}
          </div>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy || !pw}
          className="mt-5 w-full btn-orange disabled:opacity-60"
        >
          {busy ? "…" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

// ─── Product editor form ───────────────────────────────────────────
const emptyVariant = () => ({ name: "", color: "", size: "", stock: 0, model_wearing: "" });

// Kept outside ProductForm so that its identity is stable across renders
// (otherwise React unmounts the wrapped <input> on every keystroke).
const Field = ({ label, hint, children, full }) => (
  <label className={`flex flex-col gap-1 ${full ? "col-span-2" : ""}`}>
    <span className="text-xs font-badge text-[#241C18]">{label}</span>
    {hint && <span className="text-[11px] text-[#8A7568] leading-snug mb-1">{hint}</span>}
    {children}
  </label>
);
const inputClass =
  "px-3 py-2 rounded-lg border border-[#F1DCC5] bg-white text-[#241C18] outline-none focus:border-[var(--gl-orange)]";

// Reusable drag/tap-reorder tile used by both the Images and Videos grids
// in ProductForm. Supports:
//   • Native HTML5 drag-and-drop on desktop (drag any tile onto another
//     to reorder).
//   • Tap ← / → arrows on the tile for mobile / accessibility.
//   • A COVER pill on the first tile so operators know which frame
//     represents the product on collection grids and thumbnails.
//   • ✕ to remove, unchanged behaviour.
//
// `items` is the current array; `onChange` receives the reordered array.
// `renderMedia(src)` returns the actual <img> or <video> for the tile.
const MediaTile = ({ items, onChange, index, renderMedia, testidPrefix }) => {
  const move = (from, to) => {
    if (to < 0 || to >= items.length || to === from) return;
    const next = items.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  const isFirst = index === 0;
  const isLast = index === items.length - 1;

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(index));
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDrop={(e) => {
        e.preventDefault();
        const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
        if (!Number.isNaN(from)) move(from, index);
      }}
      data-testid={`${testidPrefix}-tile-${index}`}
      className="group relative aspect-[3/4] bg-white rounded-lg overflow-hidden cursor-grab active:cursor-grabbing hover:ring-2 hover:ring-[var(--gl-orange)] transition"
      title="Drag to reorder"
    >
      {renderMedia(items[index])}

      {isFirst && (
        <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-sm bg-[#241C18] text-[#F1ECE2] text-[9px] font-badge tracking-[0.16em]">
          COVER
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-1 py-1 bg-gradient-to-t from-black/55 to-transparent opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
        <button
          type="button"
          onClick={() => move(index, index - 1)}
          disabled={isFirst}
          data-testid={`${testidPrefix}-move-left-${index}`}
          aria-label="Move earlier"
          className="w-6 h-6 rounded-full bg-white/95 text-[#241C18] text-xs shadow disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ←
        </button>
        <button
          type="button"
          onClick={() => move(index, index + 1)}
          disabled={isLast}
          data-testid={`${testidPrefix}-move-right-${index}`}
          aria-label="Move later"
          className="w-6 h-6 rounded-full bg-white/95 text-[#241C18] text-xs shadow disabled:opacity-30 disabled:cursor-not-allowed"
        >
          →
        </button>
      </div>

      <button
        type="button"
        onClick={() => onChange(items.filter((_, i) => i !== index))}
        data-testid={`${testidPrefix}-remove-${index}`}
        aria-label="Remove"
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 text-[#241C18] text-xs shadow"
      >
        ✕
      </button>
    </div>
  );
};



const emptyProduct = (existing) => ({
  slug: "",
  name: "",
  sku: nextSku("tops", existing),
  category: "tops",
  price: 0,
  short_description: "",
  description: "",
  images: [],
  videos: [],
  variants: [emptyVariant()],
  shop_the_look_ids: [],
  trending: false,
  back_in_stock: false,
  novidade: false,
  show_scarcity: true,
  show_model_wearing: true,
  fabric_care: "",
  shipping_info: "",
  returns_info: "",
});

const ProductForm = ({ existing, initial, onSaved, onCancel }) => {
  const [p, setP] = useState(initial || emptyProduct(existing));
  const { categories } = useCatalog();
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isEdit = Boolean(initial);
  // Auto-sync slug to name until the admin manually touches the slug field.
  // Once touched, we respect their custom slug forever (until Reset).
  const [slugTouched, setSlugTouched] = useState(false);

  // "No options" mode — for one-of-a-kind pieces without size/colour variations.
  // We infer it from the initial data: exactly one variant with no size/colour.
  const inferNoOptions = (product) => {
    const vs = product?.variants || [];
    return vs.length === 1 && !vs[0].color && !vs[0].size;
  };
  const [noOptions, setNoOptions] = useState(inferNoOptions(initial));
  const [singleStock, setSingleStock] = useState(
    inferNoOptions(initial) ? Number(initial.variants[0].stock) || 0 : 1
  );

  // Snapshot of the stock values the admin loaded — used at save time to
  // detect which stock cells the admin actually changed. Cells they didn't
  // touch get replaced with the current backend value, so a paid order
  // that decremented stock while the form was open doesn't get reverted.
  const initialStockSnapshotRef = useMemo(() => {
    if (!initial) return { perVariant: {}, single: null };
    return {
      perVariant: Object.fromEntries(
        (initial.variants || []).map((v) => [v.name, Number(v.stock) || 0])
      ),
      single: inferNoOptions(initial) ? Number(initial.variants[0].stock) || 0 : null,
    };
  }, []);

  // Keep the "One-of-a-kind" toggle honest: switching modes must carry the
  // stock over instead of silently wiping it back to the useState default
  // of 1 (the source of the "I updated stock and now it says 0/1" bug).
  const onToggleNoOptions = (checked) => {
    if (checked) {
      // Multi-variant → single: sum current stock so nothing is lost.
      const total = (p.variants || []).reduce(
        (sum, v) => sum + (Number(v.stock) || 0),
        0
      );
      setSingleStock(total);
    } else {
      // Single → multi: seed the first variant row with the single-stock
      // value so the admin sees their number preserved.
      setP((s) => ({
        ...s,
        variants: [{ ...emptyVariant(), stock: Number(singleStock) || 0 }],
      }));
    }
    setNoOptions(checked);
  };

  useEffect(() => {
    // no-op — slug is auto-derived inline from the name field below
  }, []);

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const setVariant = (i, k, v) =>
    setP((s) => ({
      ...s,
      variants: s.variants.map((it, idx) => {
        if (idx !== i) return it;
        const next = { ...it, [k]: v };
        // Auto-refresh variant `name` when the admin edits colour or size,
        // BUT only when the name still matches its previously-auto-generated
        // value (or is blank). If the admin typed a custom label like
        // "Iberian Blue / M" we leave it alone. This kills the "I typed a
        // new size, saved, went back and the size reverted" bug: previously
        // the customer-facing name stayed stale because save-time auto-name
        // was skipped whenever `v.name` was truthy.
        if (k === "color" || k === "size") {
          const prevAuto = [it.color, it.size].filter(Boolean).join(" / ");
          if (!it.name || it.name === prevAuto) {
            next.name = [next.color, next.size].filter(Boolean).join(" / ");
          }
        }
        return next;
      }),
    }));

  const upload = async (files, kind) => {
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const { url } = await adminUploadFile(f);
        urls.push(url);
      }
      setP((s) => ({ ...s, [kind]: [...s[kind], ...urls] }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      // Build variants: either the noOptions single stock row, or filter empty rows.
      let variants = noOptions
        ? [{ name: "One size", color: "", size: "", stock: Number(singleStock) || 0, model_wearing: "" }]
        : p.variants
            .filter((v) => v.name || v.size || v.color || Number(v.stock) > 0)
            .map((v) => ({
              ...v,
              name: v.name || [v.color, v.size].filter(Boolean).join(" / ") || "One size",
              stock: Number(v.stock) || 0,
            }));

      // Stock-race protection (edit mode only). Between the admin loading
      // this form and hitting Save, a customer may have paid — decrementing
      // variant stock in Mongo. If the admin didn't touch a stock cell we
      // must NOT push the stale form value back, otherwise we'd resurrect
      // sold inventory. Re-fetch the product and, per variant, keep the
      // backend's fresh stock unless the admin changed the number.
      if (isEdit) {
        try {
          const fresh = await getProduct(initial.slug);
          const freshByName = Object.fromEntries(
            (fresh?.variants || []).map((v) => [v.name, Number(v.stock) || 0])
          );
          const snap = initialStockSnapshotRef.perVariant || {};
          variants = variants.map((v) => {
            const formStock = Number(v.stock) || 0;
            const loadedStock = snap[v.name];
            const freshStock = freshByName[v.name];
            // Admin didn't touch this stock cell (still equals what they
            // loaded) AND backend has a different current value → trust
            // the backend.
            if (
              loadedStock !== undefined &&
              freshStock !== undefined &&
              formStock === loadedStock &&
              freshStock !== loadedStock
            ) {
              return { ...v, stock: freshStock };
            }
            return v;
          });
        } catch (_e) {
          // If refetch fails just fall back to the form values — worst
          // case we lose the race protection for this save; we don't want
          // to block the admin from persisting other edits.
        }
      }

      const payload = {
        ...p,
        price: Number(p.price) || 0,
        variants: variants.length ? variants : [{ name: "One size", color: "", size: "", stock: 0, model_wearing: "" }],
      };
      if (isEdit) await adminUpdateProduct(initial.slug, payload);
      else await adminCreateProduct(payload);
      onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      data-testid="admin-product-form"
      onSubmit={submit}
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4 md:p-8"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="w-full max-w-3xl bg-[#FFF3E6] rounded-2xl border border-[#F1DCC5] shadow-xl">
        <div className="p-6 md:p-8 border-b border-[#F1DCC5] flex items-center justify-between">
          <div>
            <div className="font-badge text-[10px] tracking-[0.22em] text-[#8A7568]">
              {isEdit ? "EDIT PRODUCT" : "NEW PRODUCT"}
            </div>
            <h2 className="font-serif-display text-3xl text-[#241C18] mt-1">
              {p.name || (isEdit ? initial.name : "New item")}
            </h2>
          </div>
          <button
            type="button"
            data-testid="admin-form-close"
            onClick={onCancel}
            className="text-2xl leading-none text-[#8A7568] hover:text-[#241C18]"
          >
            ✕
          </button>
        </div>

        <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Name *" hint="Shown to shoppers, e.g. 'Vichy Patricia Dress'.">
            <input
              data-testid="admin-form-name"
              type="text"
              required
              value={p.name}
              onChange={(e) => {
                const newName = e.target.value;
                setP((s) => ({
                  ...s,
                  name: newName,
                  // Sync slug to the new name unless the admin has hand-edited
                  // the slug field this session (or it's empty).
                  slug: slugTouched && s.slug ? s.slug : slugify(newName),
                }));
              }}
              className={inputClass}
            />
          </Field>

          <Field label="Category *" hint="Where the item appears in the site navigation.">
            <select
              data-testid="admin-form-category"
              value={p.category}
              onChange={(e) => set("category", e.target.value)}
              className={inputClass}
            >
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name_pt}{c.visible === false ? " (hidden)" : ""}
                </option>
              ))}
              {p.category && !categories.some((c) => c.slug === p.category) && (
                <option value={p.category}>{p.category}</option>
              )}
            </select>
          </Field>

          <Field label="Slug" hint="URL-friendly ID — auto-synced to the name. Edit it to lock a custom URL.">
            <div className="flex items-center gap-2">
              <input
                data-testid="admin-form-slug"
                type="text"
                value={p.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  set("slug", e.target.value);
                }}
                className={inputClass + " flex-1"}
              />
              {slugTouched && (
                <button
                  type="button"
                  data-testid="admin-form-slug-reset"
                  onClick={() => {
                    setSlugTouched(false);
                    set("slug", slugify(p.name));
                  }}
                  className="text-[10px] tracking-[0.18em] uppercase text-[#8A7568] hover:text-[#241C18] underline underline-offset-2"
                  title="Reset slug to auto-sync with name"
                >
                  Reset
                </button>
              )}
            </div>
          </Field>

          <Field label="SKU" hint="Internal code for stock keeping. Auto-generated per category.">
            <input
              data-testid="admin-form-sku"
              type="text"
              value={p.sku}
              onChange={(e) => set("sku", e.target.value)}
              className={inputClass}
            />
          </Field>

          <Field label="Price (EUR) *" hint="Decimals allowed, e.g. 34.95. Do not include the € sign. Mouse-wheel is intentionally disabled so you can't nudge the price by scrolling.">
            <input
              data-testid="admin-form-price"
              type="number"
              min="0"
              step="0.01"
              required
              value={p.price}
              onChange={(e) => set("price", e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              className={`${inputClass} gl-no-num-spin`}
            />
          </Field>

          <Field label="Trending" hint="Adds a TRENDING pill and surfaces the item in the New In row.">
            <label className="flex items-center gap-2 h-10">
              <input
                type="checkbox"
                data-testid="admin-form-trending"
                checked={p.trending}
                onChange={(e) => set("trending", e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm text-[#241C18]">Mark as Trending</span>
            </label>
          </Field>

          <Field label="Back in stock" hint="Adds a BACK IN STOCK pill on the storefront card + PDP. Toggle when re-stocking a piece.">
            <label className="flex items-center gap-2 h-10">
              <input
                type="checkbox"
                data-testid="admin-form-back-in-stock"
                checked={p.back_in_stock}
                onChange={(e) => set("back_in_stock", e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm text-[#241C18]">Mark as Back in stock</span>
            </label>
          </Field>

          <Field label="Novidade" hint="Feature this piece in the Home 'New In' row and the /collection/new-in sidebar page. Toggle off once it's no longer a new arrival.">
            <label className="flex items-center gap-2 h-10">
              <input
                type="checkbox"
                data-testid="admin-form-novidade"
                checked={p.novidade}
                onChange={(e) => set("novidade", e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm text-[#241C18]">Mark as Novidade</span>
            </label>
          </Field>

          <Field label="Scarcity indicators" hint='Show the "Only N left" chip + per-variant counts for this piece when stock runs low. Uncheck to hide urgency on a specific product without affecting the sitewide switch.'>
            <label className="flex items-center gap-2 h-10">
              <input
                type="checkbox"
                data-testid="admin-form-show-scarcity"
                checked={p.show_scarcity !== false}
                onChange={(e) => set("show_scarcity", e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm text-[#241C18]">Show scarcity indicators</span>
            </label>
          </Field>

          <Field label="Short description" hint="One line under the product name. Keep it under ~90 characters." full>
            <input
              data-testid="admin-form-short_description"
              type="text"
              value={p.short_description}
              onChange={(e) => set("short_description", e.target.value)}
              className={inputClass}
            />
          </Field>

          <Field label="Long description" hint="Full product story shown on the product page. Line breaks are preserved." full>
            <textarea
              data-testid="admin-form-description"
              value={p.description}
              onChange={(e) => set("description", e.target.value)}
              rows={4}
              className={inputClass}
            />
          </Field>

          <Field label={'Show "Model is wearing…" info'} hint='When on, the per-variant "Model is wearing" text is shown on the product page. Turn off for accessories / flat-lay pieces where there is no model.' full>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                data-testid="admin-form-show-model-wearing"
                type="checkbox"
                checked={p.show_model_wearing !== false}
                onChange={(e) => set("show_model_wearing", e.target.checked)}
              />
              <span className="text-sm text-[#241C18]">Show the &quot;Model is wearing…&quot; line</span>
            </label>
          </Field>

          <Field label="Fabric & care (this product)" hint="Overrides the site-wide Fabric & Care blurb for this product only. Leave blank to use the default." full>
            <textarea
              data-testid="admin-form-fabric-care"
              value={p.fabric_care || ""}
              onChange={(e) => set("fabric_care", e.target.value)}
              rows={3}
              placeholder="e.g. 100% linho. Lavagem a frio, secagem à sombra."
              className={inputClass}
            />
          </Field>

          <Field label="Shipping (this product)" hint="Overrides the site-wide Shipping blurb for this product only. Leave blank to use the default." full>
            <textarea
              data-testid="admin-form-shipping-info"
              value={p.shipping_info || ""}
              onChange={(e) => set("shipping_info", e.target.value)}
              rows={3}
              placeholder="e.g. Envio em 24h · Grátis acima de 120€"
              className={inputClass}
            />
          </Field>

          <Field label="Returns (this product)" hint="Overrides the site-wide Returns blurb for this product only. Leave blank to use the default." full>
            <textarea
              data-testid="admin-form-returns-info"
              value={p.returns_info || ""}
              onChange={(e) => set("returns_info", e.target.value)}
              rows={3}
              placeholder="e.g. 30 dias para trocas ou devoluções."
              className={inputClass}
            />
          </Field>

          {/* Images */}
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-badge text-[#8A7568]">Images</div>
              {p.images.length > 1 && (
                <div className="text-[10px] text-[#8A7568]">
                  Drag or use ← → to reorder · first image is the cover
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {p.images.map((src, i) => (
                <MediaTile
                  key={`${src}-${i}`}
                  items={p.images}
                  onChange={(next) => set("images", next)}
                  index={i}
                  testidPrefix="admin-form-image"
                  renderMedia={(u) => (
                    <img
                      src={u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u}
                      alt=""
                      draggable={false}
                      style={{ objectPosition: imgPos(p, u) }}
                      className="w-full h-full object-cover pointer-events-none select-none"
                    />
                  )}
                />
              ))}
              <label className="aspect-[3/4] flex items-center justify-center text-sm text-[#8A7568] rounded-lg border-2 border-dashed border-[#F1DCC5] cursor-pointer hover:bg-white">
                <input
                  data-testid="admin-form-image-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => upload(e.target.files, "images")}
                />
                {uploading ? "Uploading…" : "+ Image"}
              </label>
            </div>
          </div>

          <ImageFramingEditor product={p} onChange={(next) => set("image_positions", next)} />

          {/* Videos */}
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-badge text-[#8A7568]">Videos (optional)</div>
              {p.videos.length > 1 && (
                <div className="text-[10px] text-[#8A7568]">
                  Drag or use ← → to reorder
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {p.videos.map((src, i) => (
                <MediaTile
                  key={`${src}-${i}`}
                  items={p.videos}
                  onChange={(next) => set("videos", next)}
                  index={i}
                  testidPrefix="admin-form-video"
                  renderMedia={(u) => (
                    <video
                      src={u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u}
                      className="w-full h-full object-cover pointer-events-none select-none"
                      muted
                    />
                  )}
                />
              ))}
              <label className="aspect-[3/4] flex items-center justify-center text-sm text-[#8A7568] rounded-lg border-2 border-dashed border-[#F1DCC5] cursor-pointer hover:bg-white">
                <input
                  data-testid="admin-form-video-input"
                  type="file"
                  accept="video/*"
                  multiple
                  className="hidden"
                  onChange={(e) => upload(e.target.files, "videos")}
                />
                {uploading ? "Uploading…" : "+ Video"}
              </label>
            </div>
          </div>

          {/* Variants */}
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-xs font-badge text-[#8A7568]">Variants</div>
              <label className="flex items-center gap-2 text-xs text-[#241C18] cursor-pointer select-none">
                <input
                  data-testid="admin-form-no-options-toggle"
                  type="checkbox"
                  checked={noOptions}
                  onChange={(e) => onToggleNoOptions(e.target.checked)}
                />
                One-of-a-kind piece (no size / colour options)
              </label>
            </div>

            {noOptions ? (
              <div className="p-3 bg-white rounded-lg border border-[#F1DCC5] flex items-center gap-3">
                <span className="text-xs text-[#8A7568]">Stock (single unit / total available):</span>
                <input
                  data-testid="admin-form-single-stock"
                  type="number"
                  min="0"
                  value={singleStock}
                  onChange={(e) => setSingleStock(e.target.value)}
                  className="w-24 px-2 py-1.5 rounded border border-[#F1DCC5] text-sm"
                  placeholder="1"
                />
              </div>
            ) : (
              <>
                <div className="text-[11px] text-[#8A7568] mb-2 leading-snug">
                  Leave <em>Colour</em> blank if the product only has size variants (e.g. S / M / L).
                  Leave <em>Size</em> blank if it only has colours. The <em>Name</em> is what customers see — leave blank to auto-generate from colour + size.
                </div>
                <div className="flex items-center justify-end mb-2">
                  <button
                    type="button"
                    onClick={() => set("variants", [...p.variants, emptyVariant()])}
                    className="text-xs text-[#241C18] underline underline-offset-2"
                  >
                    + Add variant
                  </button>
                </div>
                <div className="space-y-2">
                  {p.variants.map((v, i) => (
                    <div key={i} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end p-3 bg-white rounded-lg border border-[#F1DCC5]">
                      <input
                        placeholder="Name (auto from colour/size)"
                        value={v.name}
                        onChange={(e) => setVariant(i, "name", e.target.value)}
                        className="px-2 py-1.5 rounded border border-[#F1DCC5] text-sm md:col-span-2"
                      />
                      <input
                        placeholder="Colour (optional)"
                        value={v.color}
                        onChange={(e) => setVariant(i, "color", e.target.value)}
                        className="px-2 py-1.5 rounded border border-[#F1DCC5] text-sm"
                      />
                      <input
                        placeholder="Size (optional)"
                        value={v.size}
                        onChange={(e) => setVariant(i, "size", e.target.value)}
                        className="px-2 py-1.5 rounded border border-[#F1DCC5] text-sm"
                      />
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          value={v.stock}
                          onChange={(e) => setVariant(i, "stock", e.target.value)}
                          className="w-full px-2 py-1.5 rounded border border-[#F1DCC5] text-sm"
                          placeholder="Stock"
                        />
                        <button
                          type="button"
                          onClick={() => set("variants", p.variants.filter((_, idx) => idx !== i))}
                          className="text-xs text-[#8A7568] hover:text-red-600"
                          aria-label="Remove variant"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {err && (
          <div data-testid="admin-form-error" className="px-6 md:px-8 pb-4 text-sm text-red-600">
            {err}
          </div>
        )}
        <div className="p-6 md:p-8 pt-2 flex items-center justify-end gap-3 border-t border-[#F1DCC5]">
          <button type="button" onClick={onCancel} className="text-sm text-[#8A7568] hover:text-[#241C18]">
            Cancel
          </button>
          <button
            type="submit"
            data-testid="admin-form-submit"
            disabled={busy || !p.name || !p.slug}
            className="btn-orange disabled:opacity-60"
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add product"}
          </button>
        </div>
      </div>
    </form>
  );
};

// ─── Dashboard (catalog list) ─────────────────────────────────────
const AdminDashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("catalog");   // "catalog" | "orders"
  const [items, setItems] = useState([]);
  const [productStats, setProductStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product | null
  const [showNew, setShowNew] = useState(false);
  const [q, setQ] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      // Fetch catalog + 7-day traffic stats in parallel. Stats are
      // best-effort — a failure there must never block the catalog list.
      const [data, statsRes] = await Promise.all([
        adminListProducts(),
        adminGetProductStats(7).catch(() => ({ stats: {} })),
      ]);
      setItems(data);
      setProductStats(statsRes?.stats || {});
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((p) => `${p.name} ${p.sku} ${p.category}`.toLowerCase().includes(needle));
  }, [q, items]);

  const del = async (slug) => {
    if (!window.confirm(`Delete ${slug}? This can't be undone.`)) return;
    try {
      await adminDeleteProduct(slug);
      refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  // Reorder by swapping a row with its neighbour (up = -1, down = +1).
  // Optimistically mutates the local list first so the UI is instant, then
  // persists the whole slug order to the backend. We block reorder while a
  // search filter is active because moving a row inside a filtered subset
  // wouldn't make sense (the neighbour on screen isn't the neighbour in the
  // full list).
  const [reordering, setReordering] = useState(false);
  const moveProduct = async (slug, dir) => {
    if (q.trim() || reordering) return;
    const idx = items.findIndex((p) => p.slug === slug);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
    setReordering(true);
    try {
      await adminReorderProducts(next.map((p) => p.slug));
    } catch (e) {
      // Roll back on failure and surface the error to the admin.
      setItems(items);
      alert(e?.response?.data?.detail || "Reorder failed");
    } finally {
      setReordering(false);
    }
  };

  const resolveImg = (src) =>
    src && src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;

  return (
    <div className="min-h-screen bg-[#FFF3E6]">
      <header className="sticky top-0 z-10 bg-[var(--gl-orange)] text-[#241C18] px-6 py-4 flex items-center justify-between border-b border-[#E0B527]">
        <div>
          <div className="font-serif-display text-2xl">GOYA · Admin</div>
          <div className="font-badge text-[10px] tracking-[0.24em] opacity-80">CATALOG · {items.length} ITEMS</div>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" target="_blank" rel="noreferrer" className="text-xs underline underline-offset-2 hover:opacity-80">
            View site ↗
          </a>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="text-xs underline underline-offset-2 hover:opacity-80"
          >
            Log out
          </button>
        </div>
      </header>

      {/* Tab switcher */}
      <div className="bg-white border-b border-[#F1DCC5] px-6 md:px-10 flex gap-2 overflow-x-auto">
        {[
          { id: "catalog", label: `Catalog · ${items.length}` },
          { id: "orders", label: "Orders" },
          { id: "traffic", label: "Traffic" },
          { id: "content", label: "Content" },
          { id: "categories", label: "Categories" },
          { id: "sale", label: "Sale / Black Friday" },
          { id: "vouchers", label: "Vouchers" },
          { id: "emails", label: "Emails" },
          { id: "journal", label: "Journal" },
          { id: "newsletter", label: "Newsletter" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`py-3 px-4 font-badge text-[11px] tracking-[0.2em] border-b-2 transition whitespace-nowrap ${
              tab === t.id
                ? "border-[var(--gl-orange)] text-[#241C18]"
                : "border-transparent text-[#8A7568] hover:text-[#241C18]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "orders" ? (
        <OrdersDashboard onLogout={onLogout} />
      ) : tab === "traffic" ? (
        <AdminTraffic />
      ) : tab === "content" ? (
        <ContentEditor />
      ) : tab === "categories" ? (
        <AdminCategories />
      ) : tab === "sale" ? (
        <AdminSale />
      ) : tab === "vouchers" ? (
        <AdminVouchers />
      ) : tab === "emails" ? (
        <AdminEmails />
      ) : tab === "journal" ? (
        <AdminJournal />
      ) : tab === "newsletter" ? (
        <AdminNewsletter />
      ) : (
      <>
      <div className="p-6 md:p-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
          <input
            data-testid="admin-search"
            placeholder="Search by name, SKU, or category…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full md:w-80 px-4 py-2 rounded-full border border-[#F1DCC5] bg-white outline-none focus:border-[var(--gl-orange)]"
          />
          <button
            data-testid="admin-new-product"
            onClick={() => setShowNew(true)}
            className="btn-orange"
          >
            + New product
          </button>
        </div>

        {loading ? (
          <div className="text-[#8A7568]">Loading catalog…</div>
        ) : (
          <div data-testid="admin-catalog-table" className="bg-white rounded-2xl border border-[#F1DCC5] overflow-hidden">
            <div className="hidden md:grid grid-cols-[64px_80px_2fr_1fr_1fr_100px_120px] px-4 py-3 border-b border-[#F1DCC5] font-badge text-[10px] tracking-[0.2em] text-[#8A7568]">
              <div>ORDER</div>
              <div />
              <div>NAME · SKU</div>
              <div>CATEGORY</div>
              <div>PRICE</div>
              <div>TRENDING</div>
              <div>ACTIONS</div>
            </div>
            {filtered.length === 0 && (
              <div className="p-6 text-center text-[#8A7568]">No products match this search.</div>
            )}
            {q.trim() && (
              <div className="px-4 py-2 text-[11px] text-[#8A7568] bg-[#FFF3E6] border-b border-[#F1DCC5]">
                Reorder is disabled while a search is active — clear the search to move products.
              </div>
            )}
            {filtered.map((p, i) => {
              const isFirst = i === 0;
              const isLast = i === filtered.length - 1;
              const canReorder = !q.trim();
              const stat = productStats[p.slug];
              const rate = stat?.rate ?? 0;
              const hasData = (stat?.views ?? 0) > 0;
              // Colour semantics: green = strong converter, amber = mid,
              // red = attention (many views, few adds), grey = insufficient
              // data. Threshold of 3 views prevents red-flagging brand-new
              // items where 0/1 add is normal.
              const enoughData = (stat?.views ?? 0) >= 3;
              const badge = !hasData
                ? { colour: "#8A7568", bg: "#F1DCC5", label: "no data" }
                : enoughData && rate >= 0.25
                ? { colour: "#3F6A3F", bg: "#D6E8CE", label: `${Math.round(rate * 100)}%` }
                : enoughData && rate >= 0.10
                ? { colour: "#8A5A20", bg: "#FFE9D6", label: `${Math.round(rate * 100)}%` }
                : enoughData && rate < 0.10
                ? { colour: "#B03A2E", bg: "#FADBD5", label: `${Math.round(rate * 100)}%` }
                : { colour: "#8A7568", bg: "#F1DCC5", label: hasData ? `${Math.round(rate * 100)}%` : "no data" };
              return (
              <div
                key={p.id || p.slug}
                data-testid={`admin-row-${p.slug}`}
                className="grid grid-cols-[48px_64px_1fr_1fr] md:grid-cols-[64px_80px_2fr_1fr_1fr_100px_120px] items-center gap-3 md:gap-2 px-4 py-3 border-b border-[#F1DCC5] last:border-b-0"
              >
                <div className="flex flex-col items-center gap-1">
                  <button
                    type="button"
                    data-testid={`admin-move-up-${p.slug}`}
                    onClick={() => moveProduct(p.slug, -1)}
                    disabled={!canReorder || isFirst || reordering}
                    aria-label={`Move ${p.name} up`}
                    className="w-7 h-7 rounded-full border border-[#F1DCC5] flex items-center justify-center text-[#241C18] hover:border-[var(--gl-orange)] disabled:opacity-25 disabled:cursor-not-allowed"
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M6 15l6-6 6 6" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-move-down-${p.slug}`}
                    onClick={() => moveProduct(p.slug, 1)}
                    disabled={!canReorder || isLast || reordering}
                    aria-label={`Move ${p.name} down`}
                    className="w-7 h-7 rounded-full border border-[#F1DCC5] flex items-center justify-center text-[#241C18] hover:border-[var(--gl-orange)] disabled:opacity-25 disabled:cursor-not-allowed"
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </div>
                <div className="w-14 h-16 md:w-16 md:h-20 bg-[#FFE9D6] overflow-hidden rounded">
                  {p.images?.[0] && (
                    <img src={resolveImg(p.images[0])} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-serif-display text-lg text-[#241C18] truncate">{p.name}</div>
                  <div className="text-xs text-[#8A7568]">{p.sku} · /{p.slug}</div>
                  <div className="mt-1 flex items-center gap-1.5 text-[10px] font-badge tracking-[0.14em]" title="Last 7 days — unique viewers · added to cart · add rate">
                    <span className="text-[#8A7568]" data-testid={`admin-stat-views-${p.slug}`}>
                      👁 {stat?.views ?? 0}
                    </span>
                    <span className="text-[#8A7568]" data-testid={`admin-stat-adds-${p.slug}`}>
                      · 🛍 {stat?.adds ?? 0}
                    </span>
                    <span
                      data-testid={`admin-stat-rate-${p.slug}`}
                      className="ml-1 px-1.5 py-0.5 rounded"
                      style={{ background: badge.bg, color: badge.colour }}
                    >
                      {badge.label}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-[#241C18]">{p.category}</div>
                <div className="text-sm text-[#241C18]">€{Number(p.price).toFixed(2)}</div>
                <div className="text-xs">
                  {p.trending ? (
                    <span className="font-badge bg-[var(--gl-orange)] text-[#241C18] px-2 py-0.5 rounded-full text-[10px]">TRENDING</span>
                  ) : (
                    <span className="text-[#8A7568]">—</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    data-testid={`admin-edit-${p.slug}`}
                    onClick={() => setEditing(p)}
                    className="text-xs text-[#241C18] underline underline-offset-2"
                  >
                    Edit
                  </button>
                  <button
                    data-testid={`admin-delete-${p.slug}`}
                    onClick={() => del(p.slug)}
                    className="text-xs text-red-600 underline underline-offset-2"
                  >
                    Delete
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {showNew && (
        <ProductForm
          existing={items}
          onSaved={() => {
            setShowNew(false);
            refresh();
          }}
          onCancel={() => setShowNew(false)}
        />
      )}
      {editing && (
        <ProductForm
          existing={items}
          initial={editing}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
          onCancel={() => setEditing(null)}
        />
      )}
      </>
      )}
    </div>
  );
};

// ─── Root ─────────────────────────────────────────────────────────
const Admin = () => {
  const [ready, setReady] = useState(Boolean(localStorage.getItem(ADMIN_TOKEN_KEY)));

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setReady(false);
  };

  if (!ready) return <AdminLogin onLoggedIn={() => setReady(true)} />;
  return <AdminDashboard onLogout={logout} />;
};

export default Admin;
